import { optionalProtectedLoader, AreaRedirectBehaviour } from '~/shell/utils/authUtil.server';
import { useTranslation } from 'react-i18next';
import type { ClientLoaderFunctionArgs, MetaDescriptor } from 'react-router';
import { Link } from 'react-router';
import { useBasePath } from '~/common/hooks/useBasePath';
import {
  QUERY_SEARCH_PARAM_ACTION,
  QUERY_SEARCH_PARAM_NOVELTIES
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import type { Route } from './+types/AreaIndexRoute';
import { LatestNavigation } from '~/features/catalog/components/navigation/LatestNavigation';
import {
  fetchActivePgPromotions,
  fetchActiveTgPromotions,
  searchPgActionArticles,
  searchPgNoveltiesArticles,
  searchTgActionArticles,
  searchTgNoveltiesArticles
} from '~/features/startpage/startpage.server';
import { ArticleSlider } from '~/common/components/ArticleSlider';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import { Banner } from '../components/startpage/Banner';
import { promiseHash } from 'remix-utils/promise';
import { SidebarContent } from '../components/SidebarContent';
import { TgArea } from '~/common/types/area';
import { mergeRouteModuleMeta } from '~/shell/utils/metaUtil';
import { OwnBrandsNavigation } from '~/features/catalog/components/navigation/OwnBrandsNavigation';
import { getQueryClient } from '~/queryClient.ts';

export const loader = optionalProtectedLoader(
  async ({ loginInfo, request, area, t, api }) => {
    const isTgArea = area === TgArea;
    const ccId = await getSelectedLocation(request);

    const actionExclude = loginInfo?.actionExclude ?? false;
    const getActions = isTgArea
      ? searchTgActionArticles(api, request.signal, actionExclude, t)
      : searchPgActionArticles(api, request.signal, ccId, t);
    const getNovelties = isTgArea
      ? searchTgNoveltiesArticles(api, request.signal, actionExclude, t)
      : searchPgNoveltiesArticles(api, request.signal, ccId, t);

    const getPromotions = isTgArea
      ? fetchActiveTgPromotions(api, request.signal)
      : fetchActivePgPromotions(api, request.signal);

    const results = await promiseHash({
      actions: getActions,
      novelties: getNovelties,
      promotions: getPromotions
    });

    const customMetas: MetaDescriptor[] = [];
    if ('PROD' === process.env.DEPLOYMENT_ENVIRONMENT) {
      customMetas.push({ name: 'robots', content: 'all' });
    } else {
      customMetas.push({ name: 'robots', content: 'noindex, nofollow, noarchive' });
    }

    return {
      isAuthenticated: !!loginInfo,
      promotions: results.promotions,
      actions: results.actions,
      novelties: results.novelties,
      actionExclude: isTgArea ? actionExclude : false,
      meta: customMetas,
      title:
        area === TgArea
          ? t('Transgourmet Webshop | Ihr online Lebensmittel Grosshandel')
          : t('Prodega Easy | Die Produktewelt online erleben')
    };
  },
  { areaRedirectBehavior: AreaRedirectBehaviour.AreaSelection }
);

export const clientLoader = async ({ serverLoader, url }: ClientLoaderFunctionArgs) => {
  const cacheKey = ['area-index-loader', url.pathname, url.searchParams.toString()];

  const queryClient = getQueryClient();
  let searchResult: any = queryClient.getQueryData(cacheKey);

  if (!searchResult) {
    searchResult = await serverLoader();

    queryClient.setQueryData(cacheKey, searchResult, {
      updatedAt: Date.now()
    });

    queryClient.setQueryDefaults(cacheKey, {
      staleTime: 1000 * 60 * 5
    });
  }

  return searchResult;
};

export const handle = {
  // The navigation items for the current route. Will be rendered in the sub navigation area.
  subNavigationItems: () => {
    return (
      <>
        <OwnBrandsNavigation className={'mt-12'} />
        <LatestNavigation className={'mt-12'} />
      </>
    );
  }
};

export const meta: Route.MetaFunction = mergeRouteModuleMeta(({ loaderData }) => {
  return loaderData?.meta ?? [];
});

export default function AreaIndexRoute({ loaderData }: Route.ComponentProps) {
  const { t, i18n } = useTranslation();
  const { path, isAgh } = useBasePath();

  return (
    <div className="flex flex-col xl:flex-row flex-wrap gap-8 overflow-x-hidden overflow-y-auto">
      <div className="grow flex flex-col gap-8 w-full xl:flex-1">
        <Banner key={i18n.language} promotions={loaderData.promotions} />
        <h1 className="sr-only">{loaderData.title}</h1>
        {!loaderData.actionExclude && loaderData.actions.length > 0 && (
          <div className={'w-full @container flex flex-col gap-4'}>
            <div className={'flex justify-between px-2 desktop:px-0'}>
              <h2>{isAgh ? t('Top Aktionen Prodega') : t('Top Aktionen Transgourmet')}</h2>
              <Link
                className={'hover:underline hover:text-primary text-primary'}
                data-testid={'topactions-all-link'}
                to={`${path}/catalog?${QUERY_SEARCH_PARAM_ACTION}=true`}
              >
                {t('Alle ansehen')}
              </Link>
            </div>
            <ArticleSlider articles={loaderData.actions} className={'my-4 pl-4 desktop:pl-0 w-[100cqw]'} />
          </div>
        )}
        {loaderData.novelties.length > 0 && (
          <div className={'w-full @container flex flex-col gap-4'}>
            <div className={'flex justify-between px-2 desktop:px-0'}>
              <h2>{isAgh ? t('Neuheiten Prodega') : t('Neuheiten Transgourmet')}</h2>
              <Link
                className={'hover:underline hover:text-primary text-primary'}
                data-testid={'novelty-all-link'}
                to={`${path}/catalog?${QUERY_SEARCH_PARAM_NOVELTIES}=true`}
              >
                {t('Alle ansehen')}
              </Link>
            </div>
            <ArticleSlider articles={loaderData.novelties} className={'my-4 pl-4 desktop:pl-0 w-[100cqw]'} />
          </div>
        )}
      </div>
      <div className="w-full px-4 pt-4 xl:pt-0 xl:px-0 xl:w-[260px] xl:overflow-y-auto relative">
        <SidebarContent isAuthenticated={loaderData.isAuthenticated} />
      </div>
    </div>
  );
}
